import { useEffect, useMemo, useState } from "react"
import { Box } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { FetchProductGroupCodesResponse, fetchProductGroupCodes } from "../productGroupApi"
import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { ProductGroupCode, ProductGroupFormValues } from "../productGroupTypes"
import { useFormikContext } from "formik"
import { Alert, Grid } from "@mui/material"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    input: {
        backgroundColor: theme.palette.common.white,
    },
}))

function transformData(data: FetchProductGroupCodesResponse) {
    return data.productGroupCodes
}

type SelectProductGroupCodeProps = Readonly<{
    onChange?: (value: ProductGroupCode | null) => void
}>

export function SelectProductGroupCode({ onChange }: SelectProductGroupCodeProps) {
    const { setFieldValue, values } = useFormikContext<ProductGroupFormValues>()
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const [code, setCode] = useState<ProductGroupCode | undefined>(values.productGroupCode)

    const codesResult = useApiQuery(fetchProductGroupCodes, {
        queryName: "product-group-codes",
        dependencies: {
            merchantId: merchantId,
        },
        select: transformData,
    })

    useEffect(() => {
        setCode(values.productGroupCode)
    }, [values])

    const data = useMemo(() => codesResult.data ?? [], [codesResult.data])
    const selectedOption = useMemo(
        () => data?.find((code) => code.id === values?.productGroupCode?.id),
        [data, values.productGroupCode],
    )

    const disabled = data === undefined || data.length === 0
    const disabledHelperText = l10n.getString("product-group-form-code-field-helper-text-no-items")

    return (
        <Grid container direction="column" gap={3}>
            <Grid item>
                <LocalizedStrict id="product-group-form-code-field" attrs={{ label: true }}>
                    <ApiQueryAutocomplete
                        isLoading={codesResult.isLoading}
                        isError={codesResult.isError}
                        options={data}
                        disabled={disabled}
                        value={code ?? null}
                        slotProps={{
                            textField: {
                                helperText: disabled ? disabledHelperText : "",
                            },
                        }}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        getOptionLabel={(option) => (option !== null ? `${option.number} ${option.name}` : "")}
                        renderOption={(props, option) =>
                            option !== null ? (
                                <Box component="li" sx={{ ml: "0.5rem" }} {...props}>
                                    {`${option.number} ${option.name}`}
                                </Box>
                            ) : (
                                ""
                            )
                        }
                        fullWidth
                        onChange={(e, value) => {
                            setFieldValue("productGroupCode", value !== null ? value : undefined)
                            onChange && onChange(value)
                        }}
                        className={classes.input}
                    />
                </LocalizedStrict>
            </Grid>
            <Grid item>
                <LocalizedStrict id="product-group-form-code-field-helper-text">
                    <Alert severity="info">
                        Product group code is used when integrating with an accounting system. They are created by a
                        supplier admin.
                    </Alert>
                </LocalizedStrict>
            </Grid>
        </Grid>
    )
}
